import React from "react";
import { Play, Pause, SkipBack, SkipForward, Square, Shuffle, Repeat, Volume2, VolumeX, Maximize2 } from "lucide-react";
import Visualizer from "./Visualizer";
import { formatTime, pickFakeBitrate, generateFakeFilename } from "@/lib/fakeData";

export default function WinampPlayer({ audio, miniHidden, onShowMini }) {
  const {
    currentTrack, isPlaying, currentTime, duration, volume,
    toggle, stop, next, prev, seekFraction, setVolume, getFrequencyData,
  } = audio;

  const bitrate = currentTrack ? pickFakeBitrate(currentTrack) : 128;
  const trackLine = currentTrack
    ? generateFakeFilename(currentTrack)
    : "LemonWire.exe v2.7.32 — ready to rip";

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    seekFraction(frac);
  };

  const handleVol = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setVolume(frac);
  };

  return (
    <div
      data-testid="winamp-player"
      className="bg-[#0E0E14] border-2 border-t-[#4A4A5A] border-l-[#4A4A5A] border-r-[#000] border-b-[#000] p-2 flex flex-col gap-2 shadow-[inset_0_0_12px_rgba(0,0,0,0.9)]"
    >
      {/* Header label */}
      <div className="flex items-center justify-between text-[10px] text-[#B6FF00] px-1">
        <span className="font-winamp tracking-widest" style={{ fontFamily: '"VT323", monospace' }}>LEMONAMP 1.0</span>
        <div className="flex items-center gap-1.5">
          {miniHidden && (
            <button
              data-testid="player-show-mini"
              onClick={onShowMini}
              title="Re-open the sticky mini player"
              className="px-1.5 h-[16px] rounded-[2px] border border-[#4A4A5A] bg-gradient-to-b from-[#3A3A48] to-[#1A1A24] hover:from-[#4A4A58] hover:to-[#22222C] text-[9px] text-[#B6FF00] font-mono flex items-center gap-1"
            >
              <Maximize2 size={9} />
              Show Mini
            </button>
          )}
          <span className={`led-green ${isPlaying ? "animate-pulse-green" : "opacity-40"}`} />
          <span className="text-[9px] text-[#5A5A6A] font-mono">ONLINE</span>
        </div>
      </div>

      {/* LCD Display */}
      <div className="lcd-display lemon px-2 py-1.5 min-h-[62px] relative overflow-hidden" style={{ fontFamily: '"Share Tech Mono", monospace' }}>
        <div className="flex items-start justify-between text-[14px] leading-none">
          <span className="text-[10px] opacity-80">{isPlaying ? "► PLAYING" : currentTrack ? "❚❚ PAUSED" : "■ STOPPED"}</span>
          <span className="text-[10px] opacity-80">{bitrate} kbps · 44.1kHz</span>
        </div>
        <div className="marquee-outer mt-1">
          <div className={trackLine.length > 32 ? "marquee-inner text-[13px]" : "text-[13px]"} title={currentTrack ? `${currentTrack.artist} — ${currentTrack.title}` : ""}>
            {trackLine} &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp; {trackLine} &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
          </div>
        </div>
        <div className="flex justify-between text-[13px] mt-0.5">
          <span>{formatTime(currentTime)}</span>
          <span>-{formatTime(Math.max(0, (duration || 30) - currentTime))}</span>
        </div>
      </div>

      {/* Visualizer */}
      <Visualizer getFrequencyData={getFrequencyData} isPlaying={isPlaying} height={44} />

      {/* Seek bar */}
      <div
        data-testid="winamp-seek"
        onClick={handleSeek}
        className="h-2 bg-black border border-[#2A2A3A] cursor-pointer relative"
        role="slider"
        aria-valuenow={duration ? (currentTime / duration) * 100 : 0}
      >
        <div
          className="h-full bg-gradient-to-r from-[#1f8020] via-[#B6FF00] to-[#FFEB3B]"
          style={{ width: `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%` }}
        />
      </div>

      {/* Transport controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <TransportBtn data-testid="player-prev" onClick={prev} title="Previous"><SkipBack size={14} /></TransportBtn>
          <TransportBtn data-testid="player-toggle" onClick={toggle} title={isPlaying ? "Pause" : "Play"} primary>
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </TransportBtn>
          <TransportBtn data-testid="player-stop" onClick={stop} title="Stop"><Square size={12} /></TransportBtn>
          <TransportBtn data-testid="player-next" onClick={next} title="Next"><SkipForward size={14} /></TransportBtn>
        </div>

        <div className="flex items-center gap-1">
          <TransportBtn data-testid="player-shuffle" title="Shuffle" sm><Shuffle size={12} /></TransportBtn>
          <TransportBtn data-testid="player-repeat" title="Repeat" sm><Repeat size={12} /></TransportBtn>
        </div>
      </div>

      {/* Volume */}
      <div className="flex items-center gap-2 text-[#B6FF00]">
        {volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
        <div
          data-testid="player-volume"
          onClick={handleVol}
          className="flex-1 h-2 bg-black border border-[#2A2A3A] cursor-pointer"
        >
          <div
            className="h-full bg-gradient-to-r from-[#1f8020] to-[#B6FF00]"
            style={{ width: `${Math.round(volume * 100)}%` }}
          />
        </div>
        <span className="text-[10px] font-mono w-8 text-right">{Math.round(volume * 100)}%</span>
      </div>
    </div>
  );
}

function TransportBtn({ children, primary, sm, ...props }) {
  const base = sm ? "w-6 h-6" : primary ? "w-10 h-9" : "w-8 h-8";
  return (
    <button
      {...props}
      className={`${base} flex items-center justify-center rounded-sm border-2 border-t-[#6A6A7A] border-l-[#6A6A7A] border-r-black border-b-black bg-gradient-to-b from-[#4A4A5A] to-[#22222E] text-[#D0D0D0] hover:text-white active:border-t-black active:border-l-black active:border-r-[#6A6A7A] active:border-b-[#6A6A7A] active:from-[#22222E] active:to-[#4A4A5A]`}
    >
      {children}
    </button>
  );
}
